/**
 * useCampaignReach — キャンペーン × 各 type の到達者数を集計。
 * Firestore aggregation count() で 1 クエリ × 4 type = 4 クエリ取得。
 *
 * 仕様書: plan adaptive-crown §機能 6
 */
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  getCountFromServer,
  query,
  where,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

import type {
  AudienceEventType,
  CampaignReachStats,
} from '@goenhub/shared';

type CountedType = keyof CampaignReachStats;

const TYPES_TO_COUNT: CountedType[] = [
  'mail_sent',
  'line_news_received',
  'form_submitted',
  'landing_view',
];

export function useCampaignReach(campaignId: string | null | undefined) {
  return useQuery({
    queryKey: ['campaign_reach', campaignId],
    enabled: !!campaignId,
    staleTime: 60_000, // 1 分キャッシュ（集計は重いので頻繁に再取得しない）
    queryFn: async (): Promise<CampaignReachStats> => {
      if (!campaignId) {
        return {
          mail_sent: 0,
          line_news_received: 0,
          form_submitted: 0,
          landing_view: 0,
        };
      }
      const result: CampaignReachStats = {
        mail_sent: 0,
        line_news_received: 0,
        form_submitted: 0,
        landing_view: 0,
      };
      // 並列実行
      await Promise.all(
        TYPES_TO_COUNT.map(async (type) => {
          const q = query(
            collection(db, 'audience_events'),
            where('campaignId', '==', campaignId),
            where('type', '==', type),
          );
          try {
            const snap = await getCountFromServer(q);
            result[type] = snap.data().count;
          } catch (err) {
            // インデックス未作成 / 権限エラー時は 0 のまま
            console.warn(`[useCampaignReach] count failed for ${type}:`, err);
          }
        }),
      );
      return result;
    },
  });
}

/**
 * 特定リソース (formId / landingId / mailScheduleId) への到達者数を取得。
 * CampaignNode の各カードバッジ表示用。
 */
export function useTargetReach(
  campaignId: string | null | undefined,
  type: AudienceEventType | null,
  targetId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['target_reach', campaignId, type, targetId],
    enabled: !!campaignId && !!type && !!targetId,
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      if (!campaignId || !type || !targetId) return 0;
      const q = query(
        collection(db, 'audience_events'),
        where('campaignId', '==', campaignId),
        where('type', '==', type),
        where('targetId', '==', targetId),
      );
      try {
        const snap = await getCountFromServer(q);
        return snap.data().count;
      } catch (err) {
        console.warn('[useTargetReach] count failed:', err);
        return 0;
      }
    },
  });
}
